import { ENDPOINTS, ApiService, ApiServiceException } from "./ApiService";
import {
  DAARA_COOKIE_NAME,
  DAARA_REFRESH_TOKEN,
  getCookie,
  getDomain,
  isEnrolleeGatewayEnabled,
} from "./utilityService";
import { envVariables } from "../config/appConfig";
import { apiClient } from "./oidc/authConfig";
import Cookies from "js-cookie";
import { isFeatureEnabled } from "@reliance/feature-flags";
import { features } from "./constants";
import userService from "./userService";

class CareBalanceApiService extends ApiService {
  constructor() {
    super(
      getCookie(DAARA_COOKIE_NAME),
      undefined,
      process.env.REACT_APP_CARE_BALANCE_API_URL,
    );

    // Remove the old interceptor from the parent class
    this.ax.interceptors.response.eject(
      this.ax.interceptors.response.handlers[0].fulfilled,
    );

    // Add the new interceptor with the overridden method
    this.ax.interceptors.response.use(
      (res) => res,
      this.handleAuthError.bind(this),
    );
  }

  async activate(values) {
    return this.post(ENDPOINTS.careBalance.activate, values);
  }

  async getWallet(hmoId) {
    const owner = encodeURIComponent(hmoId);
    return this.get(ENDPOINTS.careBalance.getWallet(owner));
  }

  async fund(values) {
    return this.post(ENDPOINTS.careBalance.fund, values);
  }

  async getTransactions({ walletId, filter = {} }) {
    const params = {};

    for (const [key, value] of Object.entries(filter)) {
      if ([null, undefined, ""].includes(value)) {
        continue;
      }

      params[key] = value;
    }

    const transactions = await this.get(
      ENDPOINTS.careBalance.careHistory(walletId),
      params,
    );

    return transactions;
  }

  async withdraw(values) {
    return this.post(ENDPOINTS.careBalance.withdraw, values);
  }

  async getProfile() {
    return this.get(ENDPOINTS.careBalance.profile);
  }

  async getSubscription() {
    const response = await this.get(ENDPOINTS.careBalance.subscription);
    return response.data;
  }

  // override this method in the base api service
  async handleAuthError(error) {
    const originalRequest = error.config;
    const pathsToIgnore = ["/accounts/fcm-token", "/enrollees/providers", "/providers"];
    const ignoredPath = pathsToIgnore.some((path) =>
      originalRequest.url?.includes(path),
    );

    if (ignoredPath) {
      return Promise.reject(error);
    }

    if (
      (error.response?.status === 401 || error?.code === "ERR_NETWORK") &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const { accessToken, refreshToken } = await this.refreshAccessToken();
        this.accessToken = accessToken;
        this.ax.defaults.headers["Authorization"] = `Bearer ${accessToken}`;
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
        Cookies.set(DAARA_COOKIE_NAME, accessToken, { domain: getDomain() });
        Cookies.set(DAARA_REFRESH_TOKEN, refreshToken, { domain: getDomain() });
        return this.ax(originalRequest);
      } catch (refreshError) {
        this.logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
}

const patientGatewayUrl = process.env.REACT_APP_PATIENT_GATEWAY_URL;

export const isPatientGatewayEnabled = () => {
  return (
    !!patientGatewayUrl &&
    isFeatureEnabled(features.jwtToken) &&
    isFeatureEnabled(features.enablePatientGateway)
  );
};


export async function getSubscription() {
  if (isPatientGatewayEnabled()) {
    return careBalanceService.getSubscription();
  }

  return userService.getPlanDetails();
}

const careBalanceApiClient = {
  async post(url, data) {
    try {
      return await apiClient.post(url, data, { baseURL: patientGatewayUrl });
    } catch (e) {
      throw new ApiServiceException(e);
    }
  },
  async get(url, data) {
    try {
      return await apiClient.get(url, {
        params: data,
        baseURL: patientGatewayUrl,
      });
    } catch (e) {
      throw new ApiServiceException(e);
    }
  },
  activate(values) {
    return this.post(ENDPOINTS.careBalance.activate, values);
  },
  getWallet(hmoId) {
    const owner = encodeURIComponent(hmoId);
    return this.get(ENDPOINTS.careBalance.getWallet(owner));
  },
  fund(values) {
    return this.post(ENDPOINTS.careBalance.fund, values);
  },
  async getTransactions({ walletId, filter = {} }) {
    const transactions = await this.get(
      ENDPOINTS.careBalance.careHistory(walletId),
      filter,
    );
    return transactions;
  },
  withdraw(values) {
    return this.post(ENDPOINTS.careBalance.withdraw, values);
  },
  async getProfile() {
    const response = await this.get(ENDPOINTS.careBalance.profile);
    return response.data;
  },

  async getSubscription() {
    const response = await this.get(ENDPOINTS.careBalance.subscription);
    return response.data;
  },
};

const careBalanceService = envVariables.REACT_APP_FEATURE_ENABLE_KEYCLOAK_SSO
  ? careBalanceApiClient
  : new CareBalanceApiService();

export default careBalanceService;
